/**
 * Copyright 2025 ForgeRock AS. All Rights Reserved
 *
 * Use of this code requires a commercial software license with ForgeRock AS
 * or with one of its affiliates. All use shall be exclusively subject
 * to such license between the licensee and ForgeRock AS.
 */

import UAParser from 'ua-parser-js';

/**
 * Determines if the browser and OS combination is on a verified allow-list.
 *
 * @returns {boolean} True if the browser/OS combination is explicitly allowed.
 */
export function isAllowedBrowser() {
  try {
    const parser = new UAParser();
    const browserName = parser.getBrowser().name;
    const osName = parser.getOS().name;

    const allowedCombos = [
      { browser: 'Chrome', os: 'Windows' },
      { browser: 'Chrome', os: 'Mac OS' },
      { browser: 'Edge', os: 'Windows' },
      { browser: 'Edge', os: 'Mac OS' },
      { browser: 'Safari', os: 'Mac OS' },
      { browser: 'Chrome', os: 'Android' },
      { browser: 'Mobile Safari', os: 'iOS' },
    ];

    return allowedCombos.some((combo) => combo.browser === browserName && combo.os === osName);
  } catch (e) {
    return false;
  }
}

/**
 * Checks if the pendo object has been properly loaded on the window.
 * The browser must be allowed and the pendo object and initialization methods must exist.
 * The expected methods are defined in packages/platform-admin/src/services/pendoLoader.js
 *
 * @returns {boolean} True if Pendo is loaded or False if something is missing.
 */
export function isPendoLoaded() {
  return isAllowedBrowser()
    && typeof window.pendo === 'object'
    && typeof window.pendo.initialize === 'function'
    && typeof window.pendo.identify === 'function'
    && typeof window.pendo.updateOptions === 'function'
    && typeof window.pendo.pageLoad === 'function'
    && typeof window.pendo.track === 'function';
}

/**
 * Checks if Pendo has been fully initialized with visitor data.
 * This performs a comprehensive check to ensure Pendo is fully ready.
 *
 * All checks must pass for this to return true:
 * 1. The pendo object exists
 * 2. Required pendo methods exist
 * 3. A visitor ID has been set
 * 4. Pendo reports that it is ready
 *
 * @returns {boolean} True if Pendo is fully initialized with visitor data.
 * @returns {boolean} False if Pendo is not initialized.
 */
export function isPendoInitialized() {
  try {
    return isPendoLoaded()
      && typeof window.pendo.getVisitorId === 'function'
      && !!window.pendo.getVisitorId()
      && typeof window.pendo.isReady === 'function'
      && window.pendo.isReady();
  } catch {
    return false;
  }
}
